import { useEffect } from 'react';

/** Strip characters that break filesystem filenames / browser Save-as-PDF suggestions. */
export function sanitizePrintFilename(name: string): string {
  return String(name ?? '')
    .trim()
    .replace(/[/\\:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * While a print preview is open, set `document.title` so Chrome/Edge
 * "Save as PDF" suggests the document number instead of the app title.
 */
export function usePrintDocumentTitle(documentNumber?: string | null): void {
  useEffect(() => {
    const safe = sanitizePrintFilename(documentNumber ?? '');
    if (!safe) return;
    const previous = document.title;
    document.title = safe;
    return () => {
      document.title = previous;
    };
  }, [documentNumber]);
}

/**
 * Set document title for a print call, then restore on afterprint (with timeout fallback).
 * For iframe prints, pass a custom `printFn` that prints the iframe window.
 */
export function runPrintWithDocumentTitle(
  documentNumber: string | null | undefined,
  printFn: () => void = () => window.print()
): void {
  const safe = sanitizePrintFilename(documentNumber ?? '');
  const previous = document.title;
  if (safe) document.title = safe;

  let restored = false;
  const restore = (): void => {
    if (restored) return;
    restored = true;
    document.title = previous;
    window.removeEventListener('afterprint', restore);
  };

  window.addEventListener('afterprint', restore);
  try {
    printFn();
  } finally {
    window.setTimeout(restore, 5000);
  }
}
